import { UserNotification } from "@iventis/domain-model/model/userNotification";
import { NotificationResourceType } from "@iventis/domain-model/model/notificationResourceType";
import { NotificationMapExportData } from "@iventis/domain-model/model/notificationMapExportData";
import { ExportStatus } from "@iventis/domain-model/model/exportStatus";
import { MapObjectsExport } from "@iventis/domain-model/model/mapObjectsExport";
import { ExportFormat } from "@iventis/domain-model/model/exportFormat";
import { MapExportApiCreateRequest } from "@iventis/domain-model/model/mapExportApiCreateRequest";
import { OptionalExceptFor } from "@iventis/types/useful.types";
import { NotificationLayersImportData } from "@iventis/domain-model/model/notificationLayersImportData";
import { NotificationLayersCopyData } from "@iventis/domain-model/model/notificationLayersCopyData";
import { NotificationMapsCopyData } from "@iventis/domain-model/model/notificationMapsCopyData";
import { LayersCopyNotification, LayersImportNotification, MapsCopyNotification } from "./notification-types";

export enum ExportType {
    MapExportPdfPng = "MapExportPdfPng",
    LayerGeoJson = "LayerGeoJson",
    LayerKml = "LayerKml",
    LayerDwg = "LayerDwg",
    LayerExcel = "LayerExcel",
    LayerAttribute = "LayerAttribute",
}

export function createMapExportNotification(
    userId: string,
    projectId: string,
    mapExportOptions: MapExportApiCreateRequest
): UserNotification<Omit<NotificationMapExportData, "assetId">> {
    if (mapExportOptions.notificationId == null) {
        throw new Error("Map export notification id is missing");
    }

    return {
        userId,
        projectId,
        id: mapExportOptions.notificationId,
        resourceType: NotificationResourceType.MapExport,
        lastUpdatedAt: new Date(),
        resourceData: {
            exportFormat: mapExportOptions.format,
            exportStatus: ExportStatus.Requested,
            exportName: mapExportOptions.title,
        },
    };
}

export function notificationNameCreator(type: ExportType, resourceName: string) {
    switch (type) {
        case ExportType.LayerDwg:
        case ExportType.LayerGeoJson:
        case ExportType.LayerKml:
        case ExportType.LayerExcel:
            return `${resourceName} Map Objects Export`;
        case ExportType.LayerAttribute:
            return `${resourceName} attribute export`;
        default:
            throw new Error(`Export type ${type} is not supported`);
    }
}

export function createMapObjectExportNotification(
    userId: string,
    projectId: string,
    exportName: string,
    mapObjectExport: MapObjectsExport
): UserNotification<Omit<NotificationMapExportData, "assetId">> {
    return {
        userId,
        projectId,
        id: mapObjectExport.notificationId,
        resourceType: NotificationResourceType.MapExport,
        lastUpdatedAt: new Date(),
        resourceData: {
            exportFormat: mapObjectExport.exportFormat,
            exportStatus: ExportStatus.Requested,
            exportName,
        },
    };
}

export function createListItemExportNotification(
    userId: string,
    projectId: string,
    notificationId: string,
    exportName: string
): UserNotification<Omit<NotificationMapExportData, "assetId">> {
    return {
        id: notificationId,
        userId,
        projectId,
        resourceType: NotificationResourceType.MapExport,
        lastUpdatedAt: new Date(),
        resourceData: {
            exportFormat: ExportFormat.Csv,
            exportStatus: ExportStatus.Requested,
            exportName,
        },
    };
}

export function createLayersImportNotification(
    userId: string,
    projectId: string,
    importId: string,
    resourceData: OptionalExceptFor<NotificationLayersImportData, "name" | "mapId">
): LayersImportNotification {
    return {
        userId,
        projectId,
        id: importId,
        resourceType: NotificationResourceType.LayersImport,
        lastUpdatedAt: new Date(Date.now()),
        resourceData: {
            mapLayersCreated: false,
            permissionsCreated: false,
            failed: false,
            ...resourceData,
        },
    };
}

export function createLayersCopyNotification(
    userId: string,
    projectId: string,
    copyId: string,
    resourceData: OptionalExceptFor<NotificationLayersCopyData, "name" | "mapId" | "deleteOriginal">
): LayersCopyNotification {
    return {
        userId,
        projectId,
        id: copyId,
        resourceType: NotificationResourceType.LayersCopy,
        lastUpdatedAt: new Date(Date.now()),
        resourceData: {
            completed: false,
            failed: false,
            ...resourceData,
        },
    };
}
export function createMapsCopyNotification(userId: string, projectId: string, copyId: string, resourceData: NotificationMapsCopyData): MapsCopyNotification {
    return {
        userId,
        projectId,
        id: copyId,
        resourceType: NotificationResourceType.MapsCopy,
        lastUpdatedAt: new Date(Date.now()),
        resourceData,
    };
}
